import { ListingAvailability } from 'src/types/listing';

// Buyer-facing copy for a listing that can't be bought right now (the "third
// buyer" scenario: the page was open while someone else bought the car).
const UNAVAILABLE_COPY: Record<
  Exclude<ListingAvailability, ListingAvailability.Available>,
  { title: string; description: string }
> = {
  [ListingAvailability.Sold]: {
    title: 'Sold',
    description: 'This car has already been sold.',
  },
  [ListingAvailability.Reserved]: {
    title: 'Sale in progress',
    description:
      'A purchase of this car is being verified. If the payment is not confirmed, the car will become available again.',
  },
  [ListingAvailability.InEscrow]: {
    title: 'In escrow',
    description: 'This car is part of an active escrow deal.',
  },
};

export const isListingUnavailable = (
  availability: ListingAvailability | undefined
): availability is Exclude<ListingAvailability, ListingAvailability.Available> =>
  availability !== undefined && availability !== ListingAvailability.Available;

export const describeUnavailability = (
  availability: Exclude<ListingAvailability, ListingAvailability.Available>
): { title: string; description: string } => UNAVAILABLE_COPY[availability];

// The axios interceptor rejects with the API error body ({ exception, message, … }),
// not an Error instance — extract a human message from either shape.
export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error) {
    return err.message;
  }

  if (typeof err === 'object' && err !== null && 'message' in err) {
    const message = (err as { message: unknown }).message;
    if (typeof message === 'string' && message) {
      return message;
    }
  }

  if (typeof err === 'string' && err) {
    return err;
  }

  return fallback;
};

// The api lifts a machine-readable `code` to the top level of every error body
// (ApplicationExceptionFilter); older consumers read it nested in `payload`, which is
// still sent for backward compatibility. Read both.
export const getApiErrorCode = (err: unknown): string | null => {
  if (typeof err !== 'object' || err === null) {
    return null;
  }

  const { code, payload } = err as { code?: unknown; payload?: { code?: unknown } };

  if (typeof code === 'string' && code) {
    return code;
  }

  const payloadCode = payload?.code;

  return typeof payloadCode === 'string' && payloadCode ? payloadCode : null;
};

/** The wallet making the call is not linked to any account (AuthGuard `forAccount`). */
export const ACCOUNT_REQUIRED_CODE = 'ACCOUNT_REQUIRED';
